import { useCallback, useContext, useEffect, useState } from 'react'

import { FormHandles, Scope } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { AxiosProgressEvent } from 'axios'

import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import ThumbZoomable from '~/components/ThumbZoomable'
import { Badge } from '~/components/UI/Badge'
import { FileInput, FormGroup, InputHidden, Label } from '~/components/unform'
import { getImgSize } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { campaign as service } from '~/modules/retailMedia/services/industry'

import { assetSizes, MAX_SIZE_UPLOAD, MAX_SIZE_UPLOAD_BYTES } from './constants'

const FieldsetAdditionalAsset: React.FC<
  FieldsetAdditionalAssetProps<FormHandles>
> = ({ formRef, initialData, assetName, label }) => {
  const [previewMedia, setPreviewMedia] = useState<AssetPreviewMedia | null>(
    null
  )
  const [fileMedia, setFileMedia] = useState<AssetFileMedia | null>(null)

  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const { t, tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const assetSize = assetSizes[assetName]

  const handleUploadProgress = useCallback(
    () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total } = progressEvent
      const progress = Math.round((loaded * 100) / total)

      setUploadProgress(progress)
    },
    []
  )

  const handleUpload = useCallback(
    async (data: File, dimensions: Dimension) => {
      const dataMultipart = new FormData()
      dataMultipart.append('file', data)

      setUploading(true)

      try {
        const response = await service.upload({
          body: dataMultipart,
          axiosOptions: {
            onUploadProgress: handleUploadProgress
          }
        })

        setFileMedia({
          media_url: response.data.url,
          width: dimensions.width,
          height: dimensions.height
        })
      } catch (error) {
        console.error(error)
        setError(true)
        tToasts({
          prefix: 'c:messages.uploadImage.error',
          toastFunction: toast.critical
        })
      } finally {
        setUploading(false)
      }
    },
    [handleUploadProgress, tToasts, toast]
  )

  const handlePrepareToUploadImage = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setError(false)

      if (!event.target.files) return

      const handleError = (errorMessage: string) => {
        toast.critical(errorMessage)
        setPreviewMedia(null)
        setError(true)
        formRef.current.clearField(`additional_assets.${assetName}.mediaFile`)
        formRef.current.clearField(`additional_assets.${assetName}.url`)
        formRef.current.clearField(`additional_assets.${assetName}.width`)
        formRef.current.clearField(`additional_assets.${assetName}.height`)
      }

      const [file] = event.target.files

      const fileIsBigger = file.size > MAX_SIZE_UPLOAD_BYTES
      if (fileIsBigger) {
        const errorMessage = t('c:messages.maxSizeInMBPicture', {
          maxSize: MAX_SIZE_UPLOAD
        })
        handleError(errorMessage)
        return
      }

      const dimensions: Dimension = await getImgSize(file)
      const isValidSize =
        dimensions.width === assetSize.width &&
        dimensions.height === assetSize.height

      if (!isValidSize) {
        const errorMessage = t('c:messages.imageDimensions', {
          width: assetSize.width,
          height: assetSize.height
        })
        handleError(errorMessage)
        return
      }

      handleUpload(file, dimensions)

      if (previewMedia?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(previewMedia.url)
      }

      const previewURL = URL.createObjectURL(file)

      setPreviewMedia({
        type: file.type,
        url: previewURL,
        name: file.name
      })
    },
    [handleUpload, toast, formRef, t, assetName, assetSize, previewMedia?.url]
  )

  useEffect(() => {
    if (initialData) {
      setPreviewMedia({
        type: 'image',
        url: initialData.url,
        name: initialData.url.split('/').pop() || ''
      })

      setFileMedia({
        media_url: initialData.url,
        width: initialData.width,
        height: initialData.height
      })
    }
  }, [initialData])

  if (!assetSize) {
    return (
      <Alert variant="critical">
        {t('rm:campaign.additionalAssets.unknownAssetType')}
      </Alert>
    )
  }

  return (
    <FormGroup className="mb-2">
      <Label
        htmlFor={`${assetName}_mediaFile`}
        text={label}
        helperText={`${t('rm:allowedMediaSizes_one')}: ${assetSize.size}`}
      />

      {error && (
        <Alert variant="critical" className="mb-2">
          {t('rm:campaign.create.FileToUpload.error.message')}
        </Alert>
      )}

      <Scope path={`additional_assets.${assetName}`}>
        <FileInput
          name="mediaFile"
          accept="image/*"
          onChange={handlePrepareToUploadImage}
        >
          {!!previewMedia && (
            <div className="pt-3 mt-3 border-top border-tertiary">
              <ThumbZoomable
                url={previewMedia.url}
                title={previewMedia.name}
                size="medium"
              />
            </div>
          )}

          <Loading status={uploading}>
            {t('rm:campaign.create.FileToUpload.uploadingMedia')}{' '}
            {uploadProgress}%
          </Loading>

          {!previewMedia && (
            <div className="pt-3 mt-3 border-top border-tertiary">
              <h4>{t('rm:allowedMediaSizes_one')}</h4>

              <div className="d-flex gap-2 justify-content-center flex-wrap">
                <Badge template="primary">{assetSize.size}</Badge>
              </div>
            </div>
          )}
        </FileInput>

        {!!fileMedia?.media_url && (
          <>
            <InputHidden name="url" value={fileMedia.media_url} />
            <InputHidden name="width" value={fileMedia.width} />
            <InputHidden name="height" value={fileMedia.height} />
            <InputHidden name="name" value={assetName} />
          </>
        )}
      </Scope>
    </FormGroup>
  )
}

export default FieldsetAdditionalAsset
